import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface TruckCalculatorDetailsProps {
  details: {
    company_name: string | null;
    contact_person: string;
    email: string;
    phone: string | null;
    num_vehicles: number;
    km_per_year: number;
    diesel_cost_per_km: number;
    ev_cost_per_km: number;
    savings_fleet_per_year: number;
    ere_revenue_per_year: number;
    total_benefit: number;
    include_capex: boolean;
    wants_personal_proposal: boolean;
    created_at: string;
    updated_at: string;
  };
}

export function TruckCalculatorDetails({ details }: TruckCalculatorDetailsProps) {
  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('nl-NL').format(num);

  const savingsPerKm = details.diesel_cost_per_km - details.ev_cost_per_km;

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          🚛 Diesel + HBE Calculator Rapportage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Contact & Company Information */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900 border-b pb-1">Contactgegevens</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <span className="text-xs font-medium text-blue-600">Bedrijfsnaam</span>
              <p className="text-sm font-semibold text-blue-900 mt-1">
                {details.company_name || 'Niet opgegeven'}
              </p>
            </div>
            <div className="bg-indigo-50 p-3 rounded-lg">
              <span className="text-xs font-medium text-indigo-600">Contactpersoon</span>
              <p className="text-sm font-semibold text-indigo-900 mt-1">
                {details.contact_person}
              </p>
            </div>
            <div className="bg-cyan-50 p-3 rounded-lg">
              <span className="text-xs font-medium text-cyan-600">Email</span>
              <p className="text-sm font-semibold text-cyan-900 mt-1">
                {details.email}
              </p>
            </div>
            <div className="bg-teal-50 p-3 rounded-lg">
              <span className="text-xs font-medium text-teal-600">Telefoon</span>
              <p className="text-sm font-semibold text-teal-900 mt-1">
                {details.phone || 'Niet opgegeven'}
              </p>
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <span className="text-sm font-medium text-green-600">Wil persoonlijk voorstel?</span>
            <Badge className="mt-2 ml-2" variant={details.wants_personal_proposal ? 'default' : 'secondary'}>
              {details.wants_personal_proposal ? '✅ JA - WIL VOORSTEL' : 'Nee'}
            </Badge>
          </div>
        </div>

        {/* Fleet Information */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900 border-b pb-1">Vlootgegevens</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <span className="text-sm font-medium text-purple-600">Aantal voertuigen</span>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                🚛 {details.num_vehicles}
              </p>
            </div>
            <div className="bg-violet-50 p-4 rounded-lg">
              <span className="text-sm font-medium text-violet-600">Kilometers per jaar</span>
              <p className="text-2xl font-bold text-violet-900 mt-1">
                📍 {formatNumber(details.km_per_year)} km
              </p>
            </div>
          </div>
        </div>

        {/* Cost Comparison */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900 border-b pb-1">Kostenvergelijking per km</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-amber-50 p-4 rounded-lg">
              <span className="text-sm font-medium text-amber-600">⛽ Diesel</span>
              <p className="text-xl font-bold text-amber-900 mt-1">
                €{details.diesel_cost_per_km.toFixed(2)}/km
              </p>
            </div>
            <div className="bg-emerald-50 p-4 rounded-lg">
              <span className="text-sm font-medium text-emerald-600">⚡ Elektrisch</span>
              <p className="text-xl font-bold text-emerald-900 mt-1">
                €{details.ev_cost_per_km.toFixed(2)}/km
              </p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg border-2 border-green-400">
              <span className="text-sm font-medium text-green-700">💰 Besparing</span>
              <p className="text-xl font-bold text-green-900 mt-1">
                €{savingsPerKm.toFixed(2)}/km
              </p>
            </div>
          </div>
        </div>

        {/* Annual Results */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900 border-b pb-1">Jaarlijks Resultaat</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-sky-50 p-4 rounded-lg">
              <span className="text-sm font-medium text-sky-600">💰 Vlootbesparing</span>
              <p className="text-xl font-bold text-sky-900 mt-1">
                {formatCurrency(details.savings_fleet_per_year)}
              </p>
            </div>
            <div className="bg-lime-50 p-4 rounded-lg">
              <span className="text-sm font-medium text-lime-600">🌱 ERE Opbrengst</span>
              <p className="text-xl font-bold text-lime-900 mt-1">
                {formatCurrency(details.ere_revenue_per_year)}
              </p>
            </div>
          </div>
          
          {/* Total Benefit - Highlighted */}
          <div className="bg-gradient-to-r from-green-100 to-green-50 p-6 rounded-lg border-2 border-green-400">
            <div className="text-center">
              <span className="text-lg font-medium text-green-700">✅ Totaal Voordeel</span>
              <p className="text-4xl font-bold text-green-900 mt-2">
                {formatCurrency(details.total_benefit)}
              </p>
              <p className="text-sm text-green-700 mt-1">per jaar</p>
            </div>
          </div>

          {/* Flags */}
          <div className="flex gap-4 justify-center">
            <Badge variant={details.include_capex ? 'default' : 'secondary'} className="text-sm py-1 px-3">
              {details.include_capex ? '✅' : '❌'} CAPEX meegenomen
            </Badge>
            <Badge variant={details.wants_personal_proposal ? 'default' : 'secondary'} className="text-sm py-1 px-3">
              {details.wants_personal_proposal ? '✅' : '❌'} Wil voorstel
            </Badge>
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900 border-b pb-1">Metadata</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-xs font-medium text-gray-600">Aangemaakt</span>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {format(new Date(details.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <span className="text-xs font-medium text-gray-600">Bijgewerkt</span>
              <p className="text-sm font-semibold text-gray-900 mt-1">
                {format(new Date(details.updated_at), 'dd MMM yyyy HH:mm', { locale: nl })}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
